import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BuildIcon from '@mui/icons-material/Build';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState(null);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleToolsMenuOpen = (event) => {
    setToolsMenuAnchor(event.currentTarget);
  };

  const handleToolsMenuClose = () => {
    setToolsMenuAnchor(null);
  };

  const handleMobileToolsToggle = () => {
    setMobileToolsOpen(!mobileToolsOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/login');
  };

  // Main navigation items (non-Tools)
  const mainNavigationItems = [
    { text: 'Dashboard', path: '/dashboard' },
    { text: 'Analytics', path: '/analytics' },
  ];

  // Tools submenu items
  const toolsItems = [
    { text: 'Allocation Review', path: '/allocation-review' },
    { text: 'Daily Tally & Receipts', path: '/daily-tally' },
    { text: 'Compliance', path: '/compliance' },
    { text: 'Tax Helper', path: '/tax-helper' },
    { text: 'Affordability Explorer', path: '/affordability' },
    { text: 'Split Templates', path: '/split-templates' },
    { text: 'Large Purchase', path: '/large-purchase' },
    { text: 'Autopayments', path: '/autopayments' },
    { text: 'Projections', path: '/believe-it-in' },
  ];

  // Check if current path is in Tools
  const isToolsActive = toolsItems.some(item => location.pathname === item.path);

  // Mobile drawer content
  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={handleDrawerToggle}>
          <MenuIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {/* Main navigation items */}
        {mainNavigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                handleDrawerToggle();
              }}
              selected={location.pathname === item.path}
            >
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Tools accordion */}
        <ListItem disablePadding>
          <ListItemButton onClick={handleMobileToolsToggle}>
            <BuildIcon sx={{ mr: 1, fontSize: 20 }} />
            <ListItemText primary="Tools" />
            {mobileToolsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={mobileToolsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {toolsItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => {
                    navigate(item.path);
                    handleDrawerToggle();
                  }}
                  selected={location.pathname === item.path}
                >
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>

        {/* Settings */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              navigate('/settings');
              handleDrawerToggle();
            }}
            selected={location.pathname === '/settings'}
          >
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemText primary="Logout" sx={{ color: 'error.main' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/dashboard"
            sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
          >
            AlloFicient
          </Typography>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              size="large"
              sx={{ p: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              {/* Main navigation items */}
              {mainNavigationItems.map((item) => (
                <Button color="inherit" component={Link} to={item.path} key={item.text}>
                  {item.text}
                </Button>
              ))}

              {/* Tools dropdown */}
              <Button
                color="inherit"
                onClick={handleToolsMenuOpen}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: isToolsActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                }}
              >
                Tools
              </Button>
              <Menu
                anchorEl={toolsMenuAnchor}
                open={Boolean(toolsMenuAnchor)}
                onClose={handleToolsMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'tools-button',
                }}
              >
                {toolsItems.map((item) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => {
                      navigate(item.path);
                      handleToolsMenuClose();
                    }}
                    selected={location.pathname === item.path}
                  >
                    {item.text}
                  </MenuItem>
                ))}
              </Menu>

              {/* Settings */}
              <Button color="inherit" component={Link} to="/settings">
                Settings
              </Button>

              <Button color="inherit" onClick={handleLogout}>
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;
